import { useRef, useState, useEffect } from "react";
import { Send, Paperclip, Mic, Square, X, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { uploadFile, humanSize } from "@/lib/upload";
import { formatDuration } from "@/lib/constants";
import { toast } from "sonner";

export default function MessageComposer({ placeholder, onSend, testIdPrefix = "" }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const fileRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);

  const uploading = attachments.some((a) => a.status === "uploading");

  const kindOf = (type) =>
    type?.startsWith("image/") ? "image"
      : type?.startsWith("audio/") ? "audio"
      : type?.startsWith("video/") ? "video" : "file";

  const doUpload = async (file, name) => {
    const tempId = Math.random().toString(36).slice(2);
    const entry = { tempId, name: name || file.name, kind: kindOf(file.type), progress: 0, status: "uploading", att: null };
    setAttachments((a) => [...a, entry]);
    try {
      const att = await uploadFile(file, {
        name,
        onProgress: (p) => setAttachments((a) => a.map((x) => (x.tempId === tempId ? { ...x, progress: p } : x))),
      });
      setAttachments((a) => a.map((x) => (x.tempId === tempId ? { ...x, status: "done", att, progress: 1 } : x)));
    } catch (e) {
      toast.error(`Upload failed: ${name || file.name}`);
      setAttachments((a) => a.filter((x) => x.tempId !== tempId));
    }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => doUpload(f));
    e.target.value = "";
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: "audio/webm" });
        doUpload(file, file.name);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    setRecording(false);
    clearInterval(recTimerRef.current);
  };

  useEffect(() => () => clearInterval(recTimerRef.current), []);

  const removeAtt = (tempId) => setAttachments((a) => a.filter((x) => x.tempId !== tempId));

  const submit = (e) => {
    e.preventDefault();
    if (uploading) return;
    const t = text.trim();
    const done = attachments.filter((a) => a.status === "done").map((a) => a.att);
    if (!t && done.length === 0) return;
    onSend(t, done);
    setText("");
    setAttachments([]);
  };

  return (
    <form onSubmit={submit} className="p-4 border-t border-[#2E3242] bg-[#121318]">
      <div className="max-w-4xl">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a) => (
              <div key={a.tempId} className="flex items-center gap-2 pl-2 pr-1 py-1.5 rounded-lg bg-[#1E2029] border border-[#2E3242]">
                {a.kind === "image" ? <ImageIcon className="w-3.5 h-3.5 text-red-300" /> : <FileText className="w-3.5 h-3.5 text-red-300" />}
                <span className="text-xs text-gray-300 max-w-[140px] truncate">{a.name}</span>
                {a.status === "uploading" ? (
                  <span className="text-[10px] text-gray-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> {Math.round(a.progress * 100)}%
                  </span>
                ) : (
                  <button type="button" onClick={() => removeAtt(a.tempId)} className="text-gray-500 hover:text-red-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple hidden onChange={onFiles} data-testid={`${testIdPrefix}file-input`} />
          <button
            type="button"
            data-testid={`${testIdPrefix}attach-button`}
            onClick={() => fileRef.current?.click()}
            className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center border border-[#2E3242] bg-[#16181F] text-gray-400 hover:text-red-300 hover:border-red-900/40 transition-colors"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {recording ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}stop-rec-button`}
              onClick={stopRec}
              className="h-11 px-3 shrink-0 rounded-md flex items-center gap-2 border border-red-900/50 bg-red-950/60 text-red-300"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <Square className="w-4 h-4" /> {formatDuration(recSecs)}
            </button>
          ) : (
            <button
              type="button"
              data-testid={`${testIdPrefix}record-button`}
              onClick={startRec}
              className="w-11 h-11 shrink-0 rounded-md flex items-center justify-center border border-[#2E3242] bg-[#16181F] text-gray-400 hover:text-red-300 hover:border-red-900/40 transition-colors"
            >
              <Mic className="w-4 h-4" />
            </button>
          )}

          <Input
            data-testid={`${testIdPrefix}message-input`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "nearest" }), 250)}
            placeholder={placeholder}
            className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-11"
          />
          <Button
            data-testid={`${testIdPrefix}send-message-button`}
            type="submit"
            disabled={uploading || (!text.trim() && attachments.filter((a) => a.status === "done").length === 0)}
            className="ruby-gradient text-white border border-red-900/40 h-11 px-4"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </form>
  );
}
